import type { AdminLegalActor, AdminLegalDocumentType } from '@/admin/adminApi';

/**
 * Contenus legaux : ce qui ne depend ni de React ni du reseau.
 *
 * Module **pur**, comme `admin/users/routes.ts` : l'ecran et les tests
 * l'importent, et chaque regle n'est ecrite qu'ici.
 */

export const LEGAL_DOCUMENT_TYPES: readonly AdminLegalDocumentType[] = ['cgu', 'cgv'];

/** Titres tels que le mobile les affiche (`legalDocumentTitles`, `mobile/src/domains/legal/`). */
export const LEGAL_DOCUMENT_TITLES: Readonly<Record<AdminLegalDocumentType, string>> = {
  cgu: 'Conditions générales d’utilisation',
  cgv: 'Conditions générales de vente',
};

/** Sigles, pour les phrases courtes (« Publier les CGU »). */
export const LEGAL_DOCUMENT_ACRONYMS: Readonly<Record<AdminLegalDocumentType, string>> = {
  cgu: 'CGU',
  cgv: 'CGV',
};

export const isLegalDocumentType = (value: string | null): value is AdminLegalDocumentType =>
  value === 'cgu' || value === 'cgv';

/** Plafond du serveur (`AdminLegalDocumentService::MAX_CONTENT_LENGTH`). */
export const MAX_CONTENT_LENGTH = 20_000;

const SERVER_TRIM_CHARACTERS = ' \t\n\r\0\x0B';

/**
 * `trim()` de PHP : espace, tabulation, saut de ligne, retour chariot, NUL et
 * tabulation verticale, et rien d'autre. Le `trim()` de JavaScript retire en plus
 * les espaces insecables et les espaces Unicode : le serveur les garderait, et un
 * texte relu depuis lui paraitrait modifie a chaque ouverture.
 */
export const serverTrim = (text: string): string => {
  let start = 0;
  let end = text.length;

  while (start < end && SERVER_TRIM_CHARACTERS.includes(text.charAt(start))) {
    start += 1;
  }

  while (end > start && SERVER_TRIM_CHARACTERS.includes(text.charAt(end - 1))) {
    end -= 1;
  }

  return text.slice(start, end);
};

/**
 * Longueur comptee comme le serveur : apres rognage, en caracteres (points de
 * code) et non en unites UTF-16 — un emoji compte pour un.
 */
export const contentLength = (text: string): number => Array.from(serverTrim(text)).length;

/**
 * Contenu coupe en paragraphes, sur les lignes blanches.
 *
 * **Copie de `legalDocumentParagraphs`** (`mobile/src/domains/legal/domain/legalDocument.ts`),
 * qui est la source : l'apercu doit montrer le texte tel que le parent le lira,
 * paragraphe pour paragraphe. Le mobile rend du texte brut, ni Markdown ni HTML :
 * `**gras**` s'y afficherait tel quel, et l'apercu ne l'interprete pas non plus.
 *
 * Elle est **copiee** parce que le back-office n'importe rien du mobile (deux
 * applications, deux chaines de construction), et **verrouillee** par
 * `legalDocument.test.ts`, qui lit la fonction du mobile dans le depot et compare
 * ses sorties : une derive echoue au test, pas en production.
 */
export function legalDocumentParagraphs(content: string): readonly string[] {
  return content
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);
}

/** Phrase que le mobile affiche quand un document n'a aucun paragraphe. */
export const EMPTY_DOCUMENT_TEXT = 'Contenu non disponible pour le moment.';

/** Nom d'un compte d'administration ; « la migration » pour la version 1, qui n'a pas d'auteur. */
export const actorLabel = (actor: AdminLegalActor | null): string => {
  if (actor === null) {
    return 'la migration';
  }

  return actor.email ?? 'un compte supprimé';
};
